/**
 * Service Query Repository
 *
 * Handles all read operations for services.
 * This repository is responsible for fetching service data from the database.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Service } from '@/shared/types';
import { mapDatabaseServiceToService } from '@/shared/services/dataMapper';

/**
 * Database Service entity
 */
interface DatabaseService {
  id: string;
  user_id: string;
  name: string;
  rate: number;
  description?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Service Query Repository
 * Provides read-only operations for the services table
 */
export class ServiceQueryRepository extends BaseRepository<Service> {
  protected tableName = 'services' as const;

  /**
   * Find all services for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Service>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      let query = db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId);

      if (options?.filters) {
        query = this.applyFilters(query, options.filters);
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true });
      } else {
        query = query.order('name', { ascending: true });
      }

      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      const services = (data || []).map((dbService) =>
        mapDatabaseServiceToService(dbService as DatabaseService)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: services,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching services'),
      };
    }
  }

  /**
   * Find a single service by ID
   */
  async findById(id: string): Promise<RepositoryResult<Service>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Service not found',
        };
      }

      const service = mapDatabaseServiceToService(data as DatabaseService);

      return {
        success: true,
        data: service,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching service'),
      };
    }
  }

  /**
   * Find services by user ID (alias for findAll with default options)
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Service[]>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      const services = (data || []).map((dbService) =>
        mapDatabaseServiceToService(dbService as DatabaseService)
      );

      return {
        success: true,
        data: services,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching services'),
      };
    }
  }

  /**
   * Search services by name or description
   */
  async search(
    userId: string,
    query: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Service>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const searchTerm = `%${query}%`;

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`)
        .order('name', { ascending: true })
        .range(from, to);

      if (error) {
        throw error;
      }

      const services = (data || []).map((dbService) =>
        mapDatabaseServiceToService(dbService as DatabaseService)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: services,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'searching services'),
      };
    }
  }

  /**
   * Get total count of services for a user
   */
  async count(userId: string): Promise<RepositoryResult<number>> {
    try {
      const db = getDbClient();

      const { count, error } = await db
        .from(this.tableName)
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'counting services'),
      };
    }
  }

  /**
   * Find services by rate range
   */
  async findByRateRange(
    userId: string,
    minRate: number,
    maxRate: number,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Service>>> {
    try {
      const db = getDbClient();
      const { page, limit } = this.parsePagination(options);
      const { from, to } = this.getRange(page, limit);

      const { data, error, count } = await db
        .from(this.tableName)
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .gte('rate', minRate)
        .lte('rate', maxRate)
        .order('rate', { ascending: true })
        .range(from, to);

      if (error) {
        throw error;
      }

      const services = (data || []).map((dbService) =>
        mapDatabaseServiceToService(dbService as DatabaseService)
      );

      const pagination = this.buildPagination(page, limit, count || 0);

      return {
        success: true,
        data: {
          data: services,
          pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching services by rate range'),
      };
    }
  }

  /**
   * Get most popular services (most used in jobs/quotes)
   */
  async getMostPopular(
    userId: string,
    limit: number = 10
  ): Promise<RepositoryResult<Array<{ service: Service; usageCount: number }>>> {
    try {
      const db = getDbClient();

      // Get service usage counts from job_services and quote_items
      const { data: jobServices, error: jobError } = await db
        .from('job_services')
        .select('service_id')
        .select(`
          service_id,
          service:services!inner(id, user_id, name, rate, description, created_at, updated_at)
        `)
        .eq('service.user_id', userId);

      if (jobError) {
        throw jobError;
      }

      const { data: quoteItems, error: quoteError } = await db
        .from('quote_items')
        .select('service_id')
        .select(`
          service_id,
          service:services!inner(id, user_id, name, rate, description, created_at, updated_at)
        `)
        .eq('service.user_id', userId);

      if (quoteError) {
        throw quoteError;
      }

      // Count usage
      const usageMap = new Map<string, number>();

      jobServices?.forEach((js: any) => {
        const id = js.service_id;
        usageMap.set(id, (usageMap.get(id) || 0) + 1);
      });

      quoteItems?.forEach((qi: any) => {
        const id = qi.service_id;
        usageMap.set(id, (usageMap.get(id) || 0) + 1);
      });

      // Get service details and sort by usage
      const serviceIds = Array.from(usageMap.keys())
        .sort((a, b) => usageMap.get(b)! - usageMap.get(a)!)
        .slice(0, limit);

      const results = await Promise.all(
        serviceIds.map(async (serviceId) => {
          const result = await this.findById(serviceId);
          return {
            service: result.data,
            usageCount: usageMap.get(serviceId) || 0,
          };
        })
      );

      return {
        success: true,
        data: results.filter((r) => r.service),
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching popular services'),
      };
    }
  }
}

// Export singleton instance
export const serviceQueryRepository = new ServiceQueryRepository();
